import { jsx, jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
import { reduxForm } from "redux-form";
import {
  wrapDialog,
  DataTable,
  SwitchField,
  useTableEntities
} from "@teselagen/ui";
import { compose } from "redux";
import { Button, Classes, Popover } from "@blueprintjs/core";
import classNames from "classnames";
import withEditorProps from "../../withEditorProps";
import { forEach, camelCase, startCase } from "lodash-es";
import { sizeSchema } from "../PropertiesDialog/utils";
import { getRangeLength } from "@teselagen/range-utils";
import { useFormValue } from "../../utils/useFormValue";
const dialogFormName = "RemoveDuplicatesDialog";
const dataTableFormName = "duplicatesToRemove";
const checkboxStyle = { marginTop: 0, marginBottom: 0 };
const RemoveDuplicatesDialog = (props) => {
  const {
    type,
    sequenceData = { sequence: "" },
    sequenceLength,
    isProtein,
    hideModal
  } = props;
  const { selectedEntities } = useTableEntities(dataTableFormName);
  const ignoreName = useFormValue(dialogFormName, "ignoreName");
  const ignoreStartAndEnd = useFormValue(dialogFormName, "ignoreStartAndEnd");
  const ignoreStrand = useFormValue(dialogFormName, "ignoreStrand");
  const recomputeDups = useCallback(
    (values) => {
      const ignoreName2 = values?.ignoreName;
      const ignoreStartAndEnd2 = values?.ignoreStartAndEnd;
      const ignoreStrand2 = values?.ignoreStrand;
      const annotations = sequenceData[type];
      const newDups = [];
      const seqsHashByStartEndStrandName = {};
      forEach(annotations, (a) => {
        const hash = `${ignoreStartAndEnd2 ? "" : a.start}&${ignoreStartAndEnd2 ? "" : a.end}&${ignoreStrand2 ? "" : a.strand}&${ignoreName2 ? "" : a.name}`;
        if (seqsHashByStartEndStrandName[hash]) {
          newDups.push({ ...a, size: getRangeLength(a, sequenceLength) });
        } else {
          seqsHashByStartEndStrandName[hash] = true;
        }
      });
      return newDups;
    },
    [sequenceData, sequenceLength, type]
  );
  const [dups, setDups] = useState(recomputeDups);
  const selectedIds = useMemo(() => dups.map((d) => d.id), [dups]);
  const fieldSubmit = useCallback(
    (newVal, field) => {
      const values = {
        ignoreName,
        ignoreStartAndEnd,
        ignoreStrand,
        [field]: newVal
      };
      const newDups = recomputeDups(values);
      setDups(newDups);
    },
    [ignoreName, ignoreStartAndEnd, ignoreStrand, recomputeDups]
  );
  const schema = useMemo(
    () => ({
      fields: [
        { path: "name", type: "string" },
        // ...(noType ? [] : [{ path: "type", type: "string" }]),
        sizeSchema(isProtein),
        { path: "strand", type: "string" }
      ]
    }),
    [isProtein]
  );
  return /* @__PURE__ */ jsxs("div", { className: classNames(Classes.DIALOG_BODY, "tg-min-width-dialog"), children: [
    /* @__PURE__ */ jsx(
      DataTable,
      {
        noPadding: true,
        withCheckboxes: true,
        noFullscreenButton: true,
        maxHeight: 400,
        selectedIds,
        formName: dataTableFormName,
        noRouter: true,
        noRowsFoundMessage: "No duplicates found",
        compact: true,
        noHeader: true,
        noFooter: true,
        withSearch: false,
        hideSelectedCount: true,
        isInfinite: true,
        schema,
        entities: dups
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          marginTop: 10,
          display: "flex",
          justifyContent: "space-between"
        },
        children: [
          /* @__PURE__ */ jsx(
            Popover,
            {
              target: /* @__PURE__ */ jsx(Button, { icon: "settings" }),
              content: /* @__PURE__ */ jsxs("div", { style: { padding: 20, maxWidth: 250 }, children: [
                /* @__PURE__ */ jsx("div", { children: "Ignore These Fields While Finding Duplicates:" }),
                /* @__PURE__ */ jsx("br", {}),
                /* @__PURE__ */ jsx(
                  SwitchField,
                  {
                    containerStyle: { marginBottom: 2 },
                    onFieldSubmit: (newVal) => fieldSubmit(newVal, "ignoreName"),
                    style: checkboxStyle,
                    name: "ignoreName",
                    label: "Name"
                  }
                ),
                /* @__PURE__ */ jsx(
                  SwitchField,
                  {
                    containerStyle: { marginBottom: 2 },
                    onFieldSubmit: (newVal) => fieldSubmit(newVal, "ignoreStrand"),
                    style: checkboxStyle,
                    name: "ignoreStrand",
                    label: "Strand"
                  }
                ),
                /* @__PURE__ */ jsx(
                  SwitchField,
                  {
                    containerStyle: { marginBottom: 2 },
                    onFieldSubmit: (newVal) => fieldSubmit(newVal, "ignoreStartAndEnd"),
                    style: checkboxStyle,
                    name: "ignoreStartAndEnd",
                    label: "Start and End"
                  }
                )
              ] })
            }
          ),
          /* @__PURE__ */ jsxs(
            Button,
            {
              intent: "primary",
              onClick: () => {
                props[camelCase(`delete_${type}`).slice(0, -1)](
                  Object.keys(selectedEntities || {})
                );
                window.toastr.success(
                  `Successfully Deleted ${Object.keys(selectedEntities || {}).length} ${startCase(type)}`
                );
                hideModal();
              },
              disabled: !Object.keys(selectedEntities || {}).length,
              children: [
                "Remove ",
                Object.keys(selectedEntities || {}).length,
                " Duplicates"
              ]
            }
          )
        ]
      }
    )
  ] });
};
export default compose(
  wrapDialog(),
  withEditorProps,
  reduxForm({ form: dialogFormName })
)(RemoveDuplicatesDialog);
